import i18n from 'i18n-js';
import {getItemTotalQty} from '../../ScanpackScanning/utils/itemQuantities';
import { isKitItem } from './scanProgressUtils';
import { checkIsActionBarCode, checkIsRestartCode, checkClickScanEnabled, checkProductInstructions, checkSerialRecordingEnabled, validateSerialRecord } from '../../../utils/sharedValidationUtils';
import { getPostScanSteps } from '../../../utils/postScanning/postScanConfig';
export { isActionCode } from '../../../utils/sharedValidationUtils';

export { checkIsActionBarCode, checkIsRestartCode, checkClickScanEnabled, checkProductInstructions, checkSerialRecordingEnabled, validateSerialRecord };

export const checkIsValidBarcode = (barcodeInput, currentItem) => {
  if (!barcodeInput) {
    return false;
  }

  if (currentItem?.barcodes?.length > 0) {
    return currentItem.barcodes.some(
      barcodeObj =>
        barcodeObj.barcode.toLowerCase().replace(/\s+/g, ' ').trim() === 
        barcodeInput.toLowerCase().replace(/\s+/g, ' ').trim()
    );
  }

  return false;
};




export const updateScannedItemsList = (scannedItems, item) => {
  const orderItemId = item.order_item_id;
  const productId = item.product_id
  const existingItemIndex = scannedItems.findIndex(scan => scan.order_item_id === orderItemId && scan.product_id === productId);

  if (existingItemIndex !== -1) {
    const filtered = scannedItems.filter((_, index) => index !== existingItemIndex);
    return [item, ...filtered];
  } else {
    return [item, ...scannedItems];
  }
};

export const updateUnScannedItemsList = (unScannedItems, item) => {

  const orderItemId = item.order_item_id;
  const productId = item.product_id;
  return unScannedItems.reduce((acc, unScan) => {
    // Handle regular items
    if (orderItemId === unScan.order_item_id && productId === unScan.product_id ) {
      if (item.qty_remaining === 0) {
        return acc;
      } else {
        acc.push({ ...unScan, ...item });
        return acc;
      }
    }
    // Handle kit items
    if (isKitItem(unScan) && unScan.child_items && unScan.child_items.length > 0) {
      const updatedChildItems = unScan.child_items.reduce((childAcc, child) => {
        if (productId === child.product_id && item.order_item_id === unScan.order_item_id) {
          if (item.qty_remaining > 0) {
            childAcc.push({ ...child, ...item });
          }
        } else {
          childAcc.push(child);
        }
        return childAcc;
      }, []);
      if (updatedChildItems.length > 0) {
        acc.push({
          ...unScan,
          child_items: updatedChildItems
        });
      }
      return acc;
    }
    acc.push(unScan);
    return acc;
  }, []);
};

export const notFullFilledUpdatedList = (notFullFilledItems, item) => {
  const { order_item_id, product_id, skipped_qty } = item;

  return notFullFilledItems.reduce((acc, unScan) => {
    if (order_item_id === unScan.order_item_id && product_id === unScan.product_id) {
      if (skipped_qty === 0) return acc;
      acc.push({ ...unScan, ...item });
    } else {
      acc.push(unScan);
    }
    return acc;
  }, []);
};

export const updateTotePieceStatus = (tote, fromStatus="required", toStatus="picked", count = 1, incrementScannedQty = true) => {
  let updatedCount = 0;
  const updatedItemPieces = tote.item_pieces.map(piece => {
    if (piece.status === fromStatus && updatedCount < count) {
      updatedCount++;
      return {
        ...piece,
        status: toStatus,
      };
    }
    return piece;
  });

  const baseUpdate = {
    ...tote,
    item_pieces: updatedItemPieces,
    updated_at: new Date().toISOString()
  };

  if (incrementScannedQty) {
    baseUpdate.item_scanned_qty = (tote.item_scanned_qty || 0) + updatedCount;
  }

  return baseUpdate;
};

export const updateToteAssignments = (toteAssignments, updatedTote) => {
  return toteAssignments.map(tote => 
    tote.id === updatedTote.id ? updatedTote : tote
  );
};



export const findProductWithId = (productId, unScannedItems) => {
  return findItemInCollection(
    unScannedItems, 
    item => item.product_id === productId
  );
};

export const findProductWithOrderItemId = (productId, orderItemId, unScannedItems) => {
  return findItemInCollection(
    unScannedItems,
    item => item.product_id === productId && item.order_item_id === orderItemId
  );
};

const findItemInCollection = (items, predicate, includeKitChildren = true) => {
  for (const item of items) {
    if (predicate(item)) {
      return item;
    }

    if (includeKitChildren && isKitItem(item)) {
      const childItem = item.child_items.find(predicate);
      if (childItem) {
        return createKitChildItem(childItem, item.order_id);
      }
    }
  }
  return null;
};

const createKitChildItem = (childItem, parentOrderId) => ({
  ...childItem,
  order_id: parentOrderId
});

export const getCurrentTote = (previousNextPickItems,toteAssignments, nextPickItem) => {
  if(previousNextPickItems.length > 0) {
    const lastItem = previousNextPickItems[0];
    const toteAssignment = toteAssignments.find(assignment => assignment.id === lastItem.tote.id);
    return toteAssignment || null;
  }

  return nextPickItem?.tote || null;
};


export const findToteWithPickedItems = (totes, productId) => {
  return totes.find(item => {
    const hasMatchingIds = item.product_id === productId;
    const hasPicked = item.item_qty === item.item_scanned_qty;
    const hasPickedPieces = item?.item_pieces.some(
      piece => piece.status === 'picked'
    );
    return hasMatchingIds && hasPicked && hasPickedPieces;
  });
};

export const getScannedOrderItem = (orders, tote) => {
  const {order_id, order_item_id, product_id} = tote;
  const order = orders.find(order => order.id === order_id);
  if (!order) return null;
  return order.scanned_items.find(
    item =>
      item.order_item_id === order_item_id && item.product_id === product_id
  );
};

export const toteHasPickedItems = (tote) => {
  return tote.item_qty === tote.item_scanned_qty && tote?.item_pieces.some(
    piece => piece.status === 'picked'
  );
};


export const getScanLog = () => {
  return {
    input: '',
    id: '',
    product_id: 0,
    order_item_id: '',
    time: new Date().toISOString(),
    box_name: 'Box 1',
    rem_qty: null,
    qty_rem: '',
    SKU: '',
    Log_count: 1,
    product_name: '',
    name: '',
    actionBarcode: false,
    state: 'scanpack.rfp.default',
    increment_id: '',
    event: 'regular',
    updated_at: new Date().toISOString(),
    on_ex: 'on GPX',
    is_kit: false,
    kit_product_id: null
  }
};

export const getEvent = (isActionBarcode, barcodeValue) => {

  if(isActionBarcode || barcodeValue.length === 0){
    return 'click_scan';
  }
  return 'regular';
}

export const getUpdatedScanLog = (updatedItem,updatedTote,barcodeValue,incrementId, userName, isPassScan = false) => {
  const isClickScan = isPassScan || barcodeValue.length === 0;
  return {
    ...getScanLog(),
    event: getEvent(isPassScan, barcodeValue),
    input: isClickScan ? updatedItem?.barcodes[0]?.barcode : barcodeValue,
    id: updatedTote.order_id,
    SKU: updatedItem.sku,
    qty_rem: updatedItem.qty_remaining,
    rem_qty: isClickScan ? null : updatedItem.qty_remaining+1,
    increment_id: incrementId,
    product_name: updatedItem.name,
    order_item_id: updatedItem.order_item_id,
    is_kit: updatedTote.parent_product_id ? true : false,
    name: userName,
    product_id: updatedTote.parent_product_id ? updatedItem.product_id : 0
  }
}

export const getWrongScanLog = (barcodeValue, item, tote, incrementId, userName) => {
  return {
    ...getScanLog(),
    Log_count: '',
    input: barcodeValue,
    id: tote?.order_id,
    SKU: item.sku,
    qty_rem:item.qty_remaining,
    rem_qty:null,
    increment_id: incrementId,
    actionBarcode: false,
    is_kit: tote.parent_product_id ? true : false,
    name: userName,
  }
}

const createSerialLogEntry = (updatedTote, barcodeValue, updatedItem, serialInput, isSecondSerial = false, isScan = false) => {
  return {
    ask: !isSecondSerial,
    ask_2: isSecondSerial,
    barcode: barcodeValue.length === 0 ? updatedItem?.barcodes[0]?.barcode : barcodeValue,
    clicked: barcodeValue.length === 0,
    event: 'serial_scan',
    on_ex: 'on GPX',
    is_scan: isScan,
    order_id: updatedTote.order_id,
    order_item_id: updatedTote.order_item_id,
    product_id: updatedTote.product_id,
    product_lot_id: null,
    second_serial: isSecondSerial,
    serial: serialInput
  };
};
export const getSerialLog = (updatedItem,updatedTote,barcodeValue,serialRecordInput = '', serialRecordTwoInput = '') => {
  const hasFirstSerial = updatedItem.record_serial;
  const hasSecondSerial = updatedItem.second_record_serial;
  if (hasFirstSerial && hasSecondSerial) {
    return {
      serialOneLog: createSerialLogEntry(updatedTote, barcodeValue, updatedItem, serialRecordInput, false),
      serialTwoLog: createSerialLogEntry(updatedTote, barcodeValue, updatedItem, serialRecordTwoInput, true, true)
    };
  }

  if (hasFirstSerial && !hasSecondSerial) {
    return createSerialLogEntry(updatedTote, barcodeValue, updatedItem, serialRecordInput, false,true);
  }

  if (!hasFirstSerial && hasSecondSerial) {
    return createSerialLogEntry(updatedTote, barcodeValue, updatedItem, serialRecordTwoInput, true, true);
  }

  return null;
}

export const getSerialEnabledType = (item) => {
  const hasFirstSerial = item.record_serial;
  const hasSecondSerial = item.second_record_serial;
  if(hasFirstSerial && hasSecondSerial) {
    return 'both'
  }
  if(hasFirstSerial && !hasSecondSerial){
    return 'onlyFirst'
  }
  if(!hasFirstSerial && hasSecondSerial){
    return 'onlySecond'
  }

  return 'none';
}

export const generateLogsBasedOnSerialType = (
  serialEnabledType,
  updatedItem,
  updatedTote,
  barcodeValue,
  serialRecordInputOne,
  serialRecordInputTwo,
  scanLog
) => {
  switch (serialEnabledType) {
    case 'both': {
      const { serialOneLog, serialTwoLog } = getSerialLog(
        updatedItem,
        updatedTote,
        barcodeValue,
        serialRecordInputOne,
        serialRecordInputTwo
      );
      if(scanLog.event === 'regular'){
        return [serialOneLog, serialTwoLog]
      }
      return [serialOneLog, serialTwoLog, scanLog];
    }
    
    case 'onlyFirst':
    case 'onlySecond': {
      const serialLog = getSerialLog(
        updatedItem,
        updatedTote,
        barcodeValue,
        serialRecordInputOne,
        serialRecordInputTwo
      );
      if(scanLog.event === 'regular'){
        return [serialLog, scanLog]
      }
      return [serialLog];
    }

    default:
      return [scanLog];
  }
}

export const validateToteInput = (barcode, toteId, cartID) => {
  const normalizedBarcode = barcode.toLowerCase();
  const normalizedTote = toteId.toLowerCase();
  const normalizedCartID = String(cartID).toLowerCase();

  return (
    normalizedBarcode === normalizedTote ||
    normalizedBarcode === `${normalizedTote}-${normalizedCartID}`
  );
};

export const filterTotesWithPendingItems = totes => {
  return totes.filter(tote => {
    return tote.item_pieces.some(piece => piece.status === 'picked');
  });
};

export const toteContainsOrder = (tote,orderId) => {
  return (tote.assign_order_to_totes || []).some(item => item.order_id === orderId);
}

export const getToteSummary = (toteAssignments, orderId = null) => {

  if (!orderId) {
    return [];
  }

  const orderTotes = toteAssignments.filter(tote => tote.order_id === orderId);
  const toteGroups = {};

  orderTotes.forEach(tote => {
    const cartToteId = tote.cart_tote_id;
    if (!toteGroups[cartToteId]) {
      toteGroups[cartToteId] = {
        cart_tote_id: cartToteId,
        tote_id: tote.tote_id,
        row_name: tote.row_name,
        total_qty: 0,
        isPrinted: tote.isPrinted
      };
    }
    toteGroups[cartToteId].total_qty += tote.item_qty;
  });

  return Object.values(toteGroups);
};

export const getOrderIncrementId = (orders, id) => {
  return orders.find(order => order.id === id)?.increment_id
}

export const getToteOrder = (tote, orders) => {
  const orderId = tote?.assign_order_to_totes?.[0]?.order_id;
  if (orderId === undefined || orderId === null) return null;
  const orderNumber = getOrderIncrementId(orders || [], orderId);
  if (orderNumber === undefined || orderNumber === null) return null;
  return {orderId, orderNumber};
};

export const filterItemsByOrderId = (items, orderId) => {
  if (orderId === null || orderId === undefined) return items;
  if (!items) return [];
  return items.filter(item => item.order_id === orderId);
};

export const filterItemsByOrderItemIds = (items, orderItemIds) => {
  return items.filter(item => orderItemIds.includes(item.order_item_id));
};

export const getOrderItemIdsByToteId = (totes, cartToteId) => {
  return totes
    .filter(tote => tote.cart_tote_id === cartToteId)
    .map(tote => tote.order_item_id);
};

export const getOrderItemItIds = (totes, orderId) => {
  return totes
    .filter(tote => tote.order_id === orderId)
    .map(tote => tote.order_item_id);
}

export const parseToteIdentifier = (input) =>
  (input || '').toLowerCase().split('-').slice(0, -1).join('-');

export const findMatchedTote = (assignments, id) =>
  assignments?.find(t => t?.tote_id?.toLowerCase() === id);

export const buildScanPayload = (input) => ({
  app: 'app',
  input,
  scan_to_cart_enabled: true,
  state: 'scanpack.rfo'
});

export const shouldTriggerPostScan = (summary, toteIdentifier) => {
  if (!Array.isArray(summary) || summary.length === 0) return false;
  const scanned = summary.find(t => t.tote_id?.toLowerCase() === toteIdentifier);
  const alreadyPrintedCount = summary.filter(t => t.isPrinted).length;
  const isLastTote = alreadyPrintedCount === summary.length - 1;
  return Boolean(isLastTote && scanned && !scanned.isPrinted);
};

export const checkToteOrderCompletion = (toteAssignments, scannedToteId) => {
  if (!toteAssignments || !scannedToteId) {
    return {
      isComplete: false,
      orderId: null,
      totalTotes: 0,
      printedTotes: 0,
      error: 'Missing required parameters'
    };
  }
  const matchingTote = toteAssignments.find(tote =>
    tote.tote_id && scannedToteId.toLowerCase().includes(tote.tote_id.toLowerCase())
  );

  if (!matchingTote) {
    return {
      isComplete: false,
      orderId: null,
      totalTotes: 0,
      printedTotes: 0,
      error: 'Tote not found in assignments'
    };
  }

  const orderId = matchingTote.order_id;
  const orderToteSummary = getToteSummary(toteAssignments, orderId);

  if (orderToteSummary.length === 0) {
    return {
      isComplete: false,
      orderId,
      totalTotes: 0,
      printedTotes: 0,
      error: 'No totes found for order'
    };
  }

  const printedTotes = orderToteSummary.filter(tote => tote.isPrinted === true);
  const totalTotes = orderToteSummary.length;
  const isComplete = printedTotes.length === totalTotes;

  return {
    isComplete,
    orderId,
    totalTotes,
    printedTotes: printedTotes.length,
    orderToteSummary,
    scannedTote: matchingTote
  };
};

export const isNotFullFilledItem = (notFullFilledItems, item) => {
  if (!notFullFilledItems?.length) return false
  return notFullFilledItems.some(nfItem => nfItem?.order_item_id === item?.order_item_id && nfItem.product_id === item.product_id)
}

export const orderHasUnresolvedNotFulfilled = (notFullFilledItems, orderId) => {
  if (!notFullFilledItems?.length || !orderId) return false;
  return notFullFilledItems.some(nf => nf.order_id === orderId);
}

export const updateItemQty = (item, isNotFullFilled) => {
  if (isNotFullFilled) {
    return {
      ...item,
      scanned_qty: (item.scanned_qty || 0) + 1,
      skipped_qty: (item.skipped_qty || 0) - 1,
      total_qty: (item.total_qty || 0) + 1
    };
  }
  const total = getItemTotalQty(item);
  const alreadyComplete = total > 0 && (item.scanned_qty || 0) >= total;
  if (alreadyComplete) {
    return {...item, qty_remaining: Math.max(0, (item.qty_remaining || 0))};
  }
  return {
    ...item,
    scanned_qty: (item.scanned_qty || 0) + 1,
    qty_remaining: Math.max(0, (item.qty_remaining || 0) - 1)
  };
};

export function validateToteForCurrentCart(barcodeValue, toteAssignments, activeCart) {
  try {
    const barcodeParts = barcodeValue.toLowerCase().split('-');

    if (barcodeParts.length < 3) {
      return false;
    }

    const scannedCartId = barcodeParts[barcodeParts.length - 1];
    const toteIdentifier = barcodeParts.slice(0, -1).join('-');

    if (scannedCartId !== activeCart.cart_id.toString().toLowerCase()) {
      return false;
    }

    const hasToteAssignment = toteAssignments.some(tote =>
       tote?.tote_id?.toLowerCase() === toteIdentifier
    );

    if (!hasToteAssignment) {
      return false;
    }

    return true;

  } catch (error) {
    console.error('Error validating tote for pick complete:', error);
    return false;
  }
}

export const groupProductItems = (items) => {
  if (!items || items.length === 0) {
    return [];
  }

  const groupedMap = new Map();

  items.forEach(item => {
    const productId = item.product_id;

    if (!productId) {
      groupedMap.set(Symbol(), { ...item });
      return;
    }

    if (!groupedMap.has(productId)) {
      groupedMap.set(productId, { ...item });
    } else {
      const existing = groupedMap.get(productId);
      existing.qty_remaining = (existing.qty_remaining ?? 0) + (item.qty_remaining ?? 0);
      existing.total_qty = (existing.total_qty ?? 0) + (item.total_qty ?? 0);
      existing.scanned_qty = (existing.scanned_qty ?? 0) + (item.scanned_qty ?? 0);
      existing.skipped_qty = (existing.skipped_qty ?? 0) + (item.skipped_qty ?? 0);
    }
  });

  return Array.from(groupedMap.values());
};

export const findPendingReScanOrder = (cart, scannedInput = null) => {
  const pendingOrders = (cart?.cart_orders || []).filter(
    order => order?.status === 'picked' && order?.unscanned_items?.length > 0
  );
  const input = scannedInput ? String(scannedInput).trim().toLowerCase() : '';
  if (input.includes('-')) {
    return pendingOrders.find(order => order?.tote?.toLowerCase() === input) || null;
  }
  return pendingOrders[0] || null;
};

export const findOrderIdForTote = (toteAssignments, toteIdentifier) => {
  const matchedTote = toteAssignments?.find(t => t?.tote_id?.toLowerCase() === toteIdentifier);
  return matchedTote?.order_id;
};

export const dispatchScanBarcode = (dispatch, input, setIsLoading, SearchScanpackOrder) => {
  const payload = { app: 'app', input, scan_to_cart_enabled: true, state: 'scanpack.rfo' };
  dispatch(SearchScanpackOrder(payload, () => setIsLoading(false)));
};

export const handleSingleToteFlow = (
  dispatch,
  getState,
  {
    matchedOrderId,
    barcodeValue,
    setIsLoading,
    SearchScanpackOrder,
    startPostScanningForOrder
  }
) => {
  const { steps } = getPostScanSteps(getState());
  if (steps.length) {
    setIsLoading(false);
    return dispatch(startPostScanningForOrder(matchedOrderId, steps, barcodeValue));
  }
  return dispatchScanBarcode(dispatch, barcodeValue, setIsLoading, SearchScanpackOrder);
};

export const handleMatchedToteFlow = async (
  dispatch,
  getState,
  {
    matchedOrderId,
    toteAssignments,
    barcodeValue,
    setIsLoading,
    SearchScanpackOrder,
    startPostScanningForOrder
  }
) => {
  const toteSummary = getToteSummary(toteAssignments, matchedOrderId);
  const isMultiToteOrder = toteSummary && toteSummary.length > 1;
  if (isMultiToteOrder) {
    return dispatchScanBarcode(dispatch, barcodeValue, setIsLoading, SearchScanpackOrder);
  }
  return handleSingleToteFlow(dispatch, getState, {
    matchedOrderId,
    barcodeValue,
    setIsLoading,
    SearchScanpackOrder,
    startPostScanningForOrder
  });
};

export const checkIfLastOrderPostScanning = (toteAssignments, cartOrders, currentOrderId, unScannedItems) => {
  if (unScannedItems && unScannedItems.length > 0) {
    return false;
  }
  if (!cartOrders || cartOrders.length === 0 || !toteAssignments) {
    return false;
  }
  const otherOrders = cartOrders.filter(order => order.id !== currentOrderId);
  if (otherOrders.length === 0) {
    return true;
  }
  const allOtherOrdersPostScanned = otherOrders.every(order => {
    if (order.status === 'scanned') return true;
    const orderToteSummary = getToteSummary(toteAssignments, order.id);
    return orderToteSummary.length > 0 && orderToteSummary.every(tote => tote.isPrinted);
  });
  return allOtherOrdersPostScanned;
};

export const isValidToteBarcode = (input) => {
  if (!input) return false;
  const parts = input.split('-');
  return parts.length >= 2;
};

export const isFullyPickedTote = (tote) => {
  return (
    typeof tote?.item_qty === 'number' &&
    typeof tote?.item_scanned_qty === 'number' &&
    tote.item_qty > 0 &&
    tote.item_qty === tote.item_scanned_qty
  );
};

export const findScannedToteAndOrder = (toteAssignments, lastCartInput) => {
  if (lastCartInput && isValidToteBarcode(lastCartInput)) {
    const result = checkToteOrderCompletion(toteAssignments, lastCartInput);
    if (result?.orderId && result?.scannedTote) {
      return { orderId: result.orderId, scannedTote: result.scannedTote };
    }
  }

  const fullyPickedTote = toteAssignments.find(isFullyPickedTote);
  if (fullyPickedTote?.order_id) {
    return { orderId: fullyPickedTote.order_id, scannedTote: fullyPickedTote };
  }

  return null;
};

export const canShowProcessNowButton = (settings, scanPackSettings, toteAssignments, cartOrders, lastCartInput) => {
  const scanPackWorkflow = settings?.data?.scan_pack_workflow;
  const isScanToCartWorkflow = scanPackWorkflow === 'scan_to_cart' && !!scanPackSettings?.scan_to_cart_option;
  const isProcessNowEnabled = !!scanPackSettings?.process_order_during_picking;

  if (!isScanToCartWorkflow || !isProcessNowEnabled) {
    return false;
  }

  if (!Array.isArray(toteAssignments) || toteAssignments.length === 0) {
    return false;
  }

  if (!Array.isArray(cartOrders) || cartOrders.length === 0) {
    return false;
  }

  const toteAndOrder = findScannedToteAndOrder(toteAssignments, lastCartInput);
  if (!toteAndOrder) {
    return false;
  }

  const { orderId, scannedTote } = toteAndOrder;

  if (!isFullyPickedTote(scannedTote)) {
    return false;
  }

  const currentOrder = cartOrders.find(order => order.id === orderId);
  const validStatuses = ['picked', 'scanned'];
  if (!currentOrder || !validStatuses.includes(currentOrder.status)) {
    return false;
  }

  const orderToteSummary = getToteSummary(toteAssignments, orderId);
  const allTotesPrinted = orderToteSummary.length > 0 && orderToteSummary.every(tote => tote.isPrinted);

  return !allTotesPrinted;
};

export const getProcessNowConfig = (toteAssignments, lastCartInput) => {
  const toteAndOrder = findScannedToteAndOrder(toteAssignments, lastCartInput);
  if (!toteAndOrder) {
    return null;
  }

  return {
    orderId: toteAndOrder.orderId,
    toteInput: lastCartInput || null
  };
};

export const getTotalUnScannedCount = (items) => {
  const totalUnScannedCount = items.reduce((count, item) => {
    if (item.product_type === 'individual' && item.child_items?.length > 0) {
      return (
        count +
        item.child_items.reduce(
          (childCount, child) => childCount + (child.qty_remaining || 0),
          0
        )
      );
    }
    return count + (item.qty_remaining || 0);
  }, 0);
  return totalUnScannedCount;
}

export const getUnscannedPieceCount = items => getTotalUnScannedCount(groupProductItems(items));

export const getScannedPieceCount = items =>
  items.reduce((count, item) => count + (item.scanned_qty || 0), 0);

export const isTypeScanDisabled = value => value === 'Disable' || value === 'off';

export const findKitParentForItem = (currentItem, unScannedItems = []) => {
  if (!currentItem?.kit_product_id) return null;
  return (
    unScannedItems.find(
      item =>
        item.order_item_id === currentItem.order_item_id &&
        item.child_items?.some(child => child.product_id === currentItem.product_id)
    ) || null
  );
};

export const checkCartTypeScanCode = (barcodeValue, scanPackSettings, currentItem, typeScanState, unScannedItems = []) => {
  if (!scanPackSettings?.type_scan_code_enabled || scanPackSettings?.type_scan_code !== barcodeValue) {
    return null;
  }

  if (!currentItem) return null;

  if (typeScanState !== currentItem.sku) {
    return {
      showPrompt: true,
      message: i18n.t('scanpackItem.Scanpack_type_count_msg'),
      playError: true
    };
  }

  const parentItem = findKitParentForItem(currentItem, unScannedItems);

  if (
    isTypeScanDisabled(currentItem?.type_scan_enabled) ||
    (parentItem && isTypeScanDisabled(parentItem?.type_scan_enabled))
  ) {
    return {
      showPrompt: true,
      message: i18n.t('scanpackItem.Disable_type_count_msg'),
      playError: true
    };
  }

  return {showPrompt: false};
};

export const getCartBulkScanLog = (updatedItem, updatedTote, count, incrementId, userName, event = 'type_scan') => {
  return {
    ...getScanLog(),
    event,
    input: updatedItem?.barcodes?.[0]?.barcode || '',
    id: updatedTote?.order_id,
    SKU: updatedItem?.sku,
    qty_rem: updatedItem?.qty_remaining,
    rem_qty: (updatedItem?.qty_remaining || 0) + count,
    increment_id: incrementId,
    product_name: updatedItem?.name,
    actionBarcode: false,
    order_item_id: updatedItem?.order_item_id,
    is_kit: !!updatedTote?.parent_product_id,
    count,
    name: userName,
    product_id: updatedTote?.parent_product_id ? updatedItem?.product_id : 0
  };
};

export const allocateTypeScanPieces = (count, startPick, unScannedItems, scannedItems, toteAssignments, findNextPick) => {
  const allocations = [];
  let workingUnScanned = [...unScannedItems];
  let workingScanned = [...scannedItems];
  let workingTotes = [...toteAssignments];
  let current = startPick;
  let remaining = count;
  const productId = startPick?.item?.product_id;
  const visitedToteIds = new Set();

  while (remaining > 0 && current?.item && current?.tote) {
    if (visitedToteIds.has(current.tote.id)) break;
    visitedToteIds.add(current.tote.id);

    const itemRemaining = Math.max(0, current.item.qty_remaining || 0);
    const toteRemaining = Math.max(0, (current.tote.item_qty || 0) - (current.tote.item_scanned_qty || 0));
    const take = Math.min(remaining, itemRemaining, toteRemaining);

    if (take > 0) {
      let updatedItem = current.item;
      for (let i = 0; i < take; i += 1) {
        updatedItem = updateItemQty(updatedItem, false);
      }
      const updatedTote = updateTotePieceStatus(current.tote, 'required', 'picked', take, true);

      workingScanned = updateScannedItemsList(workingScanned, updatedItem);
      workingUnScanned = updateUnScannedItemsList(workingUnScanned, updatedItem);
      workingTotes = updateToteAssignments(workingTotes, updatedTote);

      allocations.push({item: updatedItem, tote: updatedTote, count: take});
      remaining -= take;

      if (remaining <= 0) break;
    }

    current = findNextPick(workingUnScanned, workingTotes, productId);
    if (!current?.item || !current?.tote) break;
  }

  return {allocations, workingUnScanned, workingScanned, workingTotes, unallocated: remaining};
};

export const mergePendingPlacements = (previousNextPickItems, allocations) => {
  const merged = [...previousNextPickItems];
  allocations.forEach(({item, tote}) => {
    const existingIndex = merged.findIndex(
      pick => pick.item.product_id === item.product_id && pick.item.order_item_id === item.order_item_id
    );
    if (existingIndex >= 0) {
      merged[existingIndex] = {item, tote};
    } else {
      merged.push({item, tote});
    }
  });
  return merged;
};
